import React, { useEffect, useState } from 'react';
import { Save, Loader2, Palette, Building2 } from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { teamsApi } from '../services/creatorApi';
import { toast } from 'sonner';
export function Settings() {
  const {
    currentWorkspace,
    refreshTeams
  } = useWorkspace();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: currentWorkspace.name,
    logoUrl: currentWorkspace.logoUrl || '',
    primaryColor: currentWorkspace.primaryColor || '#2563eb'
  });
  useEffect(() => {
    setForm({
      name: currentWorkspace.name,
      logoUrl: currentWorkspace.logoUrl || '',
      primaryColor: currentWorkspace.primaryColor || '#2563eb'
    });
  }, [currentWorkspace]);
  const handleSave = async () => {
    if (!currentWorkspace.creatorId) {
      toast.error('No team record found — create a team in the Regions module first');
      return;
    }
    setSaving(true);
    try {
      await teamsApi.update(currentWorkspace.creatorId, {
        Team_Name: form.name,
        Logo_URL: form.logoUrl,
        Primary_Color: form.primaryColor
      } as any);
      toast.success('Settings saved');
      await refreshTeams();
    } catch (err) {
      console.error('Failed to save settings:', err);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };
  return <div className="space-y-6" data-id="element-5950">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-5951">
        <div className="min-w-0" data-id="element-5952">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-5953">
            Team Settings
          </h1>
          <p className="mt-1 text-sm text-gray-500" data-id="element-5954">
            Configure branding for {currentWorkspace.name}.
          </p>
        </div>
        <button onClick={handleSave} disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 whitespace-nowrap flex-shrink-0" data-id="element-5955">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" data-id="element-5956" /> : <Save className="h-4 w-4 flex-shrink-0" data-id="element-5957" />}
          <span data-id="element-5958">Save Changes</span>
        </button>
      </div>

      <div className="space-y-6 max-w-4xl" data-id="element-5959">
        {/* Branding Section */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 sm:p-6 space-y-4" data-id="element-5960">
          <div className="flex items-center gap-2 mb-2" data-id="element-5961">
            <Building2 className="h-5 w-5 text-gray-400" data-id="element-5962" />
            <h3 className="text-lg font-medium text-gray-900" data-id="element-5963">Branding</h3>
          </div>
          <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-5964">
            <div className="sm:col-span-4" data-id="element-5965">
              <label className="block text-sm font-medium text-gray-700" data-id="element-5966">
                Team Name
              </label>
              <input type="text" value={form.name} onChange={e => setForm({
              ...form,
              name: e.target.value
            })} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" data-id="element-5967" />
            </div>
            <div className="sm:col-span-6" data-id="element-5968">
              <label className="block text-sm font-medium text-gray-700" data-id="element-5969">
                Logo URL
              </label>
              <input type="url" value={form.logoUrl} onChange={e => setForm({
              ...form,
              logoUrl: e.target.value
            })} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" placeholder="https://..." data-id="element-5970" />
              {form.logoUrl && <div className="mt-2" data-id="element-5971">
                  <img src={form.logoUrl} alt="Team logo preview" className="h-12 object-contain rounded border border-gray-200 bg-gray-50 p-1" onError={e => {
                ;
                (e.target as HTMLImageElement).style.display = 'none';
              }} data-id="element-5972" />
                </div>}
            </div>
            <div className="sm:col-span-3" data-id="element-5973">
              <label className="block text-sm font-medium text-gray-700" data-id="element-5974">
                Primary Color
              </label>
              <div className="mt-1 flex items-center gap-3" data-id="element-5975">
                <input type="color" value={form.primaryColor} onChange={e => setForm({
                ...form,
                primaryColor: e.target.value
              })} className="h-9 w-9 rounded border border-gray-300 cursor-pointer flex-shrink-0" data-id="element-5976" />
                <input type="text" value={form.primaryColor} onChange={e => setForm({
                ...form,
                primaryColor: e.target.value
              })} className="block w-32 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm font-mono" placeholder="#2563eb" data-id="element-5977" />
              </div>
              <div className="mt-3 flex items-center gap-3" data-id="element-5978">
                <div className="h-8 px-4 rounded-md flex items-center text-white text-xs font-medium" style={{
                backgroundColor: form.primaryColor
              }} data-id="element-5979">
                  Preview Button
                </div>
                <div className="h-8 w-8 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{
                backgroundColor: form.primaryColor
              }} data-id="element-5980">
                  A
                </div>
                <span className="text-xs text-gray-500" data-id="element-5981">
                  Used for sidebar accents, active states, and avatars
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Info card */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-600" data-id="element-5982">
          <p data-id="element-5983">
            <strong data-id="element-5984">Regions</strong> are managed in the{' '}
            <a href="/regions" className="text-blue-600 hover:text-blue-700 underline" data-id="element-5985">
              Regions
            </a>{' '}
            module.
            <strong className="ml-3" data-id="element-5986">Users</strong> are managed in the{' '}
            <a href="/users" className="text-blue-600 hover:text-blue-700 underline" data-id="element-5987">
              Team Users
            </a>{' '}
            module.
          </p>
        </div>
      </div>
    </div>;
}